import React, { useEffect, useMemo, useState } from 'react';
import { lifecycleDevices } from '../AreaOverview/DeviceLifecycleOnboarding';
import AndroidPowerPolicyDiagnostics from '../AreaOverview/AndroidPowerPolicyDiagnostics';
import { devicePowerPolicy } from '../AreaOverview/nativeStandbyPermission';
import PowerMainPanel from './PowerMainPanel';
import { powerMethodLabel } from '../AreaOverview/powerMethods';
import { confirmedTvMethods } from './powerDiscovery';
import { loadSiteControlSnapshot } from './siteControlApi';
import useScreenOperation from './useScreenOperation';
import SetupPanel from '../AreaOverview/SetupPanel';
import ScreenPowerSchedules from './ScreenPowerSchedules';
import ScreenOperationAlert from './ScreenOperationAlert';
import SitePowerRoutines from './SitePowerRoutines';
import SiteOperationProgress from './SiteOperationProgress';
import useSiteOperation from './useSiteOperation';
import '../AreaOverview/AreaOverview.css';

export function mergeSiteControlTargets(devices, targets) {
  const targetsByDeviceId = new Map(
    (targets || []).map((target) => [String(target.deviceId || target.id), target])
  );
  const merged = (devices || []).map((device) => {
    const target = targetsByDeviceId.get(String(device.id));
    if (!target) return device;
    targetsByDeviceId.delete(String(device.id));
    return { ...device, ...target, id: device.id };
  });
  return [
    ...merged,
    ...Array.from(targetsByDeviceId.values(), (target) => ({
      ...target,
      id: target.deviceId || target.id,
    })),
  ];
}

export function powerSetupSummary(topology) {
  const screens = topology?.screens || [];
  const methodStatus = (screen, phase) => {
    const methods = (screen?.activationStrategy?.powerProfile?.[phase]?.methods || []).filter((method) => method.enabled !== false);
    if (methods.some((method) => method.verification?.status === 'confirmed')) return 'ready';
    if (methods.length) return 'needs-test';
    return 'not-started';
  };
  const statusForAll = (phase) => {
    if (!screens.length) return 'not-started';
    const statuses = screens.map((screen) => methodStatus(screen, phase));
    if (statuses.every((status) => status === 'ready')) return 'ready';
    if (statuses.some((status) => status !== 'not-started')) return 'needs-test';
    return 'not-started';
  };
  const connectionsReady = screens.length > 0 && screens.every((screen) => (
    screen.displayKind === 'unmanaged'
      ? (screen.players || []).length > 0
      : Boolean(screen.televisionDeviceId)
  ));
  return {
    screenCount: screens.length,
    connections: connectionsReady ? 'ready' : 'not-started',
    startup: statusForAll('startup'),
    shutdown: statusForAll('shutdown'),
  };
}

export function buildScreenPowerView(screen, devices) {
  if (!screen) return null;
  const devicesById = new Map((devices || []).map((device) => [Number(device.id), device]));
  const television = devicesById.get(Number(screen.televisionDeviceId));
  const players = (screen.players || []).map((player) => {
    const id = Number(player.deviceId || player.id);
    const device = devicesById.get(id);
    return {
      id,
      name: device?.name || player.name || `Device ${id}`,
      input: player.expectedInput || null,
    };
  });
  const methodsFor = (phase) => (
    screen.activationStrategy?.powerProfile?.[phase]?.methods || []
  ).filter((method) => method.enabled !== false).map((method) => {
    const targetId = Number(method.targetDeviceId || (
      method.targetRole === 'player' ? screen.preferredPlayerDeviceId : screen.televisionDeviceId
    ));
    return {
      id: method.id,
      label: powerMethodLabel(method.type, phase),
      targetName: devicesById.get(targetId)?.name || `Device ${targetId}`,
      status: method.verification?.status || 'untested',
      effects: Object.entries(method.effects || {})
        .filter(([, value]) => value === 'yes')
        .map(([key]) => key),
    };
  });
  return {
    id: screen.id,
    name: screen.name || television?.name || `Screen ${screen.id}`,
    television: screen.displayKind === 'unmanaged' ? null : {
      id: Number(screen.televisionDeviceId),
      name: television?.name || screen.televisionName || `TV ${screen.televisionDeviceId}`,
    },
    players,
    startup: methodsFor('startup'),
    shutdown: methodsFor('shutdown'),
  };
}

export function savedRoutineSummary(screen, view) {
  const lifecycle = screen.activationStrategy?.deviceLifecycleProfile?.devices || {};
  const postStart = screen.activationStrategy?.postStart || {};
  const deviceName = (id) => view.players.find((player) => player.id === Number(id))?.name
    || (view.television?.id === Number(id) ? view.television.name : `Device ${id}`);
  const methodText = (phase) => {
    const tvIds = new Set(confirmedTvMethods(screen, phase).map((method) => method.id));
    const methods = view[phase].filter((method) => method.status === 'confirmed' && tvIds.has(method.id));
    return methods.length ? methods.map((method) => `${method.label} via ${method.targetName}`).join('; ') : 'No tested method saved';
  };
  const deviceText = (phase) => {
    if (!view.players.length) return 'No connected devices';
    return view.players.map((player) => {
      const config = lifecycle[player.id]?.[phase];
      const behaviour = config?.strategy === 'follows_device' ? `follows ${deviceName(config.dependsOnDeviceId)}`
        : config?.strategy === 'direct_method' ? 'own power command'
          : config?.strategy === 'always_on' ? 'stays available'
            : config?.strategy === 'manual' ? 'manual action' : 'left unchanged (not configured)';
      return `${player.name}: ${behaviour}`;
    }).join(' · ');
  };
  return {
    startup: methodText('startup'), shutdown: methodText('shutdown'),
    devicesOn: deviceText('startup'), devicesOff: deviceText('shutdown'),
    target: postStart.activatePreferredSource
      ? `Select ${deviceName(screen.preferredPlayerDeviceId)}${view.players.find((player) => player.id === Number(screen.preferredPlayerDeviceId))?.input ? ` on ${view.players.find((player) => player.id === Number(screen.preferredPlayerDeviceId)).input}` : ''}`
      : 'TV input stays unchanged',
    launch: postStart.launchSuitestream ? `Launch Suitestream on ${deviceName(postStart.launchTargetDeviceId)}` : 'No initial app launch saved',
  };
}

function RoutineCard({ phase, ready, busy, onRun, expanded, onEdit, setupBusy }) {
  return (
    <article className={`screen-power-routine screen-power-routine--${phase} ${ready ? 'is-ready' : ''}`}>
      <header>
        <button type="button" className="screen-power-run" disabled={busy || !ready} onClick={onRun}
          aria-label={phase === 'startup' ? 'Start this Screen' : 'Stop this Screen'}
          title={ready ? 'Run the saved Screen routine' : 'Confirm a power method first'}>{phase === 'startup' ? '▶ Start' : '■ Stop'}</button>
        <div><strong>{phase === 'startup' ? 'Start this Screen' : 'Stop this Screen'}</strong><small>{ready ? 'Saved method tested · routine safety checked when run' : 'Teach Suitestream what works'}</small></div>
        <div className="screen-power-routine__setup">
          <em>{ready ? 'Method learned' : 'Setup needed'}</em>
          <button type="button" disabled={setupBusy} onClick={onEdit} aria-expanded={expanded} aria-controls="screen-power-editor"
            aria-label={`${ready ? 'Edit' : 'Setup'} ${phase}`}>{ready ? 'Edit' : 'Setup'}</button>
        </div>
      </header>
    </article>
  );
}

function PowerControlTab({ device, area, siteId, devices = [], topology, onTopologyChange, onOpenInputTraining, onOpenPowerLog, initialSetupPhase, diagnosticsRequest }) {
  const [siteControlTargets, setSiteControlTargets] = useState([]);
  const [editingPhase, setEditingPhase] = useState(null);
  const [setupBusy, setSetupBusy] = useState(false);
  const [workspace, setWorkspace] = useState('screen');
  const siteExecution = useSiteOperation(siteId);
  useEffect(() => { if (initialSetupPhase || diagnosticsRequest) setWorkspace('screen'); }, [initialSetupPhase, diagnosticsRequest]);
  useEffect(() => {
    if (diagnosticsRequest) document.getElementById('android-power-policy-diagnostics')?.scrollIntoView?.({ block: 'nearest' });
  }, [diagnosticsRequest]);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setSiteControlTargets([]);
      return undefined;
    }
    loadSiteControlSnapshot(siteId)
      .then((snapshot) => {
        if (!cancelled) setSiteControlTargets(snapshot?.targets || []);
      })
      .catch(() => {
        if (!cancelled) setSiteControlTargets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const powerDevices = useMemo(
    () => mergeSiteControlTargets(devices, siteControlTargets),
    [devices, siteControlTargets]
  );
  const screens = useMemo(() => (topology?.screens || []).filter((screen) => screen.id), [topology]);
  const selectedScreen = area?.id ? screens.find((screen) => device?.id != null
    && String(screen.televisionDeviceId) === String(device.id)) || null : null;
  useEffect(() => { setEditingPhase(initialSetupPhase || null); setSetupBusy(false); }, [area?.id, selectedScreen?.id, initialSetupPhase]);
  const execution = useScreenOperation(area?.id, selectedScreen?.id);
  const screenPowerView = useMemo(
    () => buildScreenPowerView(selectedScreen, powerDevices),
    [powerDevices, selectedScreen]
  );
  const routineSummary = useMemo(() => selectedScreen && screenPowerView
    ? savedRoutineSummary(selectedScreen, screenPowerView) : null, [selectedScreen, screenPowerView]);

  if (!siteId && !area?.id) {
    return <div className="dashboard-network-empty">Select a site to configure power control.</div>;
  }

  return (
    <div className="area-overview__setup site-control__power-control">
      <nav className="power-control-workspaces" aria-label="Power Control workspace">
        <button type="button" aria-pressed={workspace === 'screen'} onClick={() => setWorkspace('screen')}>Screen routines</button>
        <button type="button" aria-pressed={workspace === 'site'} onClick={() => setWorkspace('site')}>Site routines</button>
      </nav>
      <div hidden={workspace !== 'site'}>
        <SitePowerRoutines key={siteId || 'no-site'} siteId={siteId} execution={siteExecution}
          externalBusy={execution.busy || setupBusy} onOpenPowerLog={onOpenPowerLog} />
      </div>
      <div hidden={workspace !== 'screen'}>
        {siteExecution.busy && <SiteOperationProgress {...siteExecution} />}
        {!area?.id && <p>Select an area to configure Screen routines.</p>}
        <section className="screen-power-workspace" aria-labelledby="screen-power-title">
          <header className="screen-power-workspace__hero">
            <div>
              <span className="screen-power-workspace__eyebrow">Turn on · start playing · turn off</span>
              <h2 id="screen-power-title">Screen power</h2>
              <p>
                A Screen is your TV and its connected devices. Start or stop them together using
                the saved routine; keep recovery relays and shared players available.
              </p>
            </div>
            {onOpenPowerLog && <button type="button" onClick={onOpenPowerLog}>View Power Log</button>}
            <div className="screen-power-workspace__count">
              <strong>{screens.length}</strong>
              <span>{screens.length === 1 ? 'Screen' : 'Screens'} in this area</span>
            </div>
          </header>
        </section>
        {screenPowerView && <section className="screen-power-overview" aria-label="Selected Screen power routines">
          <header>
            <div><span>Selected Screen</span><h3>{screenPowerView.name}</h3></div>
          </header>
          <div className="screen-power-overview__topology">
            {screenPowerView.television && <div className="screen-power-overview__tv"><span>TV</span><strong>{screenPowerView.television.name}</strong></div>}
            {screenPowerView.players.map((player) => <React.Fragment key={player.id}><span className="screen-power-overview__link">{player.input || 'HDMI port unknown'} →</span><div className="screen-power-overview__player"><span>Source</span><strong>{player.name}</strong></div></React.Fragment>)}
            {!screenPowerView.players.length && <p>{screenPowerView.television ? 'Standalone TV · no attached players' : 'No devices attached to this Screen yet'}</p>}
          </div>
          <div className="screen-power-overview__routines">
            {['startup', 'shutdown'].map((phase) => <RoutineCard key={phase} phase={phase}
              ready={confirmedTvMethods(selectedScreen, phase).length > 0} busy={execution.busy || setupBusy || siteExecution.busy} setupBusy={setupBusy || siteExecution.busy}
              onRun={() => execution.run(phase === 'startup' ? 'start' : 'stop')} expanded={editingPhase === phase}
              onEdit={() => setEditingPhase(editingPhase === phase ? null : phase)} />)}
          </div>
          <div className="screen-power-overview__plan" aria-label="Saved Screen routine">
            <div><span>01 · START</span><strong>TV first</strong><p>{routineSummary.startup}</p></div>
            <div><span>02 · PREPARE</span><strong>Connected devices and input</strong><p>{routineSummary.devicesOn}</p><p>{routineSummary.target}</p></div>
            <div><span>03 · PLAY</span><strong>Initial launch</strong><p>{routineSummary.launch}</p><p>The scheduler handles later launches.</p></div>
            <div><span>04 · STOP</span><strong>Shutdown plan</strong><p>{routineSummary.devicesOff}</p><p>{routineSummary.shutdown}</p></div>
          </div>
        </section>}
        {selectedScreen && <ScreenOperationAlert key={execution.operation?.id || 'no-operation'} {...execution} screen={selectedScreen} devices={powerDevices} busy={execution.busy || siteExecution.busy}
          onSetup={setEditingPhase} onLog={onOpenPowerLog} onRun={execution.run} onReview={execution.review} />}
        {screens.length > 0 && !selectedScreen && <p className="dashboard-network-empty">Select a TV configured as a Screen using the main device dropdown at the top right.</p>}
        {!screens.length && <p className="dashboard-network-empty">Create a Screen in Virtual Matrix first, then return here to teach its power routine.</p>}
        {selectedScreen && editingPhase && <section id="screen-power-editor" className="screen-power-editor" aria-labelledby="screen-power-editor-title">
          <header><h3 id="screen-power-editor-title">Setup and test · {editingPhase === 'startup' ? 'Startup' : 'Shutdown'} · {screenPowerView.name}</h3>
            <button type="button" disabled={setupBusy} onClick={() => setEditingPhase(null)}>Close setup</button></header>
          <PowerMainPanel key={`${area.id}-${selectedScreen.id}-${editingPhase}`} areaId={area.id} screen={selectedScreen} phase={editingPhase}
            topology={topology} devices={powerDevices} onTopologyChange={onTopologyChange} busy={setupBusy || execution.busy || siteExecution.busy} onBusyChange={setSetupBusy} onOpenInputTraining={onOpenInputTraining} />
        </section>}
        {area?.id && <SetupPanel key={`policy-${area.id}-${device?.id || 'all'}`} id="android-power-policy-diagnostics" defaultOpen={Boolean(diagnosticsRequest)} title="Android Power Policy Diagnostics"
          description="Android settings reported by devices in this area">
          <AndroidPowerPolicyDiagnostics areaId={area.id} screenId={selectedScreen?.id} devices={selectedScreen && !diagnosticsRequest ? lifecycleDevices(selectedScreen, powerDevices)
            : powerDevices.filter((entry) => !(diagnosticsRequest?.deviceId || device?.id) || String(entry.id) === String(diagnosticsRequest?.deviceId || device.id)).map((entry) => ({
              ...entry, role: entry.type || 'device', powerPolicy: devicePowerPolicy(entry),
            }))} />
        </SetupPanel>}
        {selectedScreen && <ScreenPowerSchedules key={`schedule-${area.id}-${selectedScreen.id}`} areaId={area.id} screenId={selectedScreen.id} screenName={screenPowerView.name} />}
      </div>
    </div>
  );
}

export default PowerControlTab;
